import { useRef, useState, useEffect } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Search } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { ContactItem } from "./contactItem"

function ContactSkeleton() {
  return <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
    <Skeleton className="h-10 w-10 rounded-full shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      <Skeleton className="h-3.5 w-32" />
      <Skeleton className="h-3 w-48" />
    </div>
  </div>
}

interface ContactListProps {
  contacts: Contact[]
  selectedContactId: string | null
  loading: boolean
  onContactSelected: (id: string) => void
}

export function ContactList({ contacts, selectedContactId, loading, onContactSelected }: ContactListProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [query, setQuery] = useState("")
  const [filtered, setFiltered] = useState(contacts)

  useEffect(() => {
    setFiltered(contacts)
  }, [contacts])

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value
    setQuery(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const lower = q.toLowerCase()
      setFiltered(q ? contacts.filter(c => c.name?.toLowerCase().includes(lower)) : contacts)
    }, 300)
  }

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 73,
    overscan: 5,
  })

  return <div className="w-80 shrink-0 flex flex-col min-h-0 border-r border-border">
    <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
      <div className="flex items-center gap-2 bg-muted rounded-md px-3 py-1.5 flex-1">
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={query}
          onChange={handleSearchChange}
          placeholder="Search contacts…"
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-hidden"
        />
      </div>
    </div>

    <div ref={listRef} className="flex-1 overflow-y-auto">
      {loading ? (
        <div>
          {Array.from({ length: 6 }).map((_, i) => <ContactSkeleton key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">No contacts</div>
      ) : (
        <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
          {virtualizer.getVirtualItems().map(virtualItem => {
            const contact = filtered[virtualItem.index]
            return (
              <div key={contact.id} style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${virtualItem.start}px)` }}>
                <ContactItem
                  contact={contact}
                  selected={selectedContactId === contact.id}
                  onSelect={onContactSelected}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  </div>
}
